import { Plus, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api';
import { AppHeader } from './components/AppHeader';
import { CalendarView, type DateParts } from './components/CalendarView';
import { ConfirmDialog } from './components/ConfirmDialog';
import { EditorSheet } from './components/EditorSheet';
import { FilterRow } from './components/FilterRow';
import { LoginScreen } from './components/LoginScreen';
import { MainTabs } from './components/MainTabs';
import { ReminderView } from './components/ReminderView';
import { SettingsDialog } from './components/SettingsDialog';
import { TodoView } from './components/TodoView';
import { SYNC_INTERVAL_MS, TAGS } from './constants';
import { useSwipe } from './hooks/useSwipe';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { applyTheme } from './themes';
import type { CalendarEvent, EditableItem, MainView, Preferences, Reminder, SyncState, ThemeMode, Todo } from './types';
import { dateKey, isActiveReminder, isClosed, isExpired, todayParts } from './utils';

type DeleteTarget = { kind: 'todo'; item: Todo } | { kind: 'reminder'; item: Reminder } | { kind: 'calendar'; item: CalendarEvent };
type SavePayload = Record<string, unknown>;

const views: MainView[] = ['todos', 'reminders', 'calendar'];

export default function App() {
  const today = todayParts();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncCycle, setSyncCycle] = useState(0);
  const [error, setError] = useState('');
  const [view, setView] = useState<MainView>(() => (localStorage.getItem('pwa:view') as MainView) || 'todos');
  const [viewDirection, setViewDirection] = useState<-1 | 1>(1);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [todoFilter, setTodoFilter] = useState('open');
  const [reminderFilter, setReminderFilter] = useState('all');
  const [todos, setTodos] = useState<Todo[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [editor, setEditor] = useState<EditableItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState<Preferences>({
    deviceId: 'default-device', themeId: localStorage.getItem('pwa:theme') || 'hatsune',
    themeMode: (localStorage.getItem('pwa:mode') as ThemeMode) || 'system', revision: 0, updatedAt: 0,
  });
  const [selectedDate, setSelectedDate] = useState<DateParts>(today);
  const [visibleMonth, setVisibleMonth] = useState({ year: today.year, month: today.month });
  const [monthDirection, setMonthDirection] = useState<-1 | 1>(1);

  const loadAll = useCallback(async (quiet = false) => {
    if (!quiet) setSyncState('syncing');
    setSyncCycle((cycle) => cycle + 1);
    try {
      const [nextTodos, nextReminders, nextEvents, nextPreferences] = await Promise.all([
        api.listTodos(), api.listReminders(), api.listCalendarEvents(), api.getPreferences(),
      ]);
      setTodos(nextTodos);
      setReminders(nextReminders);
      setEvents(nextEvents);
      setPreferences(nextPreferences);
      localStorage.setItem('pwa:data', JSON.stringify({ todos: nextTodos, reminders: nextReminders, events: nextEvents }));
      setAuthenticated(true);
      setError('');
      setSyncState('success');
      window.setTimeout(() => setSyncState('idle'), 3_000);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setAuthenticated(false);
      } else {
        const cached = localStorage.getItem('pwa:data');
        if (cached && !todos.length && !reminders.length && !events.length) {
          try {
            const parsed = JSON.parse(cached);
            setTodos(parsed.todos || []); setReminders(parsed.reminders || []); setEvents(parsed.events || []);
          } catch { /* ignore invalid cache */ }
        }
        setError('無法連線到同步服務');
        setSyncState('error');
        window.setTimeout(() => setSyncState('idle'), 3_000);
      }
    } finally {
      setLoading(false);
    }
  }, [events.length, reminders.length, todos.length]);

  useEffect(() => {
    void (async () => {
      try {
        const status = await api.sessionStatus();
        if (!status.configured || !status.authenticated) {
          setAuthenticated(false);
          setLoading(false);
          return;
        }
        await loadAll();
      } catch {
        setAuthenticated(false);
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authenticated) return;
    const timer = window.setInterval(() => {
      if (!document.hidden && !busy) void loadAll(true);
    }, SYNC_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [authenticated, busy, loadAll]);

  useEffect(() => {
    applyTheme(preferences.themeId, preferences.themeMode);
    localStorage.setItem('pwa:theme', preferences.themeId);
    localStorage.setItem('pwa:mode', preferences.themeMode);
  }, [preferences]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => preferences.themeMode === 'system' && applyTheme(preferences.themeId, 'system');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [preferences.themeId, preferences.themeMode]);

  const changeView = useCallback((next: MainView) => {
    if (next === view) return;
    setViewDirection(views.indexOf(next) > views.indexOf(view) ? 1 : -1);
    setView(next);
    localStorage.setItem('pwa:view', next);
  }, [view]);

  const mainSwipe = useSwipe(
    () => { const next = views[views.indexOf(view) + 1]; if (next) changeView(next); },
    () => { const previous = views[views.indexOf(view) - 1]; if (previous) changeView(previous); },
  );

  const counts = useMemo(() => ({
    todos: todos.filter((item) => item.isEnabled && !item.isDone).length,
    reminders: reminders.filter(isActiveReminder).length,
    calendar: events.filter((item) => item.isEnabled).length,
  }), [events, reminders, todos]);

  const todoFilters = [
    { value: 'open', label: '未完成', count: counts.todos },
    { value: 'done', label: '已完成', count: todos.filter((item) => item.isDone).length },
    { value: 'closed', label: '已關閉', count: todos.filter((item) => !item.isEnabled && !item.isDone).length },
  ];
  const expiredCount = reminders.filter(isExpired).length;
  const closedCount = reminders.filter(isClosed).length;
  const reminderFilters = [
    { value: 'all', label: '全部', count: counts.reminders },
    ...TAGS.map((tag) => ({ value: tag.value, label: tag.label, color: tag.color, hidden: !reminders.some((item) => item.tag === tag.value && isActiveReminder(item)) })),
    { value: 'expired', label: '已結束', color: '#ef5350', hidden: expiredCount === 0 },
    { value: 'closed', label: '已關閉', color: '#7d8894', hidden: closedCount === 0 },
  ];

  async function mutate(action: () => Promise<void>) {
    setBusy(true);
    setSyncState('syncing');
    try {
      await action();
      setError('');
      setSyncState('success');
      setSyncCycle((cycle) => cycle + 1);
      window.setTimeout(() => setSyncState('idle'), 3_000);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setError('資料已在其他裝置修改，已重新同步');
        await loadAll(true);
      } else if (caught instanceof ApiError && caught.status === 401) {
        setAuthenticated(false);
      } else {
        setError(caught instanceof Error ? caught.message : '操作失敗');
      }
      setSyncState('error');
    } finally {
      setBusy(false);
    }
  }

  async function saveEditor(payload: SavePayload) {
    if (!editor) return;
    await mutate(async () => {
      if (editor.kind === 'todo') {
        if (editor.item && !editor.duplicate) {
          const updated = await api.updateTodo({ ...editor.item, ...payload } as Todo);
          setTodos((items) => items.map((item) => item.id === updated.id ? updated : item));
        } else {
          const created = await api.createTodo({
            syncId: crypto.randomUUID(), isDone: false, isEnabled: true,
            createdAt: Date.now(), completedAt: null, ...payload,
          } as Partial<Todo>);
          setTodos((items) => [created, ...items]);
        }
      }
      if (editor.kind === 'reminder') {
        if (editor.item && !editor.duplicate) {
          const updated = await api.updateReminder({ ...editor.item, ...payload } as Reminder);
          setReminders((items) => items.map((item) => item.id === updated.id ? updated : item));
        } else {
          const created = await api.createReminder({
            syncId: crypto.randomUUID(), isEnabled: true, repeatType: 'none', tag: 'default', advanceMinutes: 0, ...payload,
          } as Partial<Reminder>);
          setReminders((items) => [...items, created]);
        }
      }
      if (editor.kind === 'calendar') {
        if (editor.item && !editor.duplicate) {
          const updated = await api.updateCalendarEvent({ ...editor.item, ...payload } as CalendarEvent);
          setEvents((items) => items.map((item) => item.id === updated.id ? updated : item));
        } else {
          const created = await api.createCalendarEvent({
            syncId: crypto.randomUUID(), isEnabled: true, createdAt: Date.now(), tag: 'default', color: 'blue', ...payload,
          } as Partial<CalendarEvent>);
          setEvents((items) => [...items, created]);
        }
      }
      setEditor(null);
    });
  }

  async function toggleTodo(todo: Todo, field: 'isDone' | 'isEnabled') {
    await mutate(async () => {
      const now = Date.now();
      const patch = field === 'isDone'
        ? { isDone: !todo.isDone, completedAt: !todo.isDone ? now : null }
        : { isEnabled: !todo.isEnabled };
      const updated = await api.updateTodo({ ...todo, ...patch });
      setTodos((items) => items.map((item) => item.id === updated.id ? updated : item));
    });
  }

  async function toggleReminder(reminder: Reminder) {
    await mutate(async () => {
      const updated = await api.updateReminder({ ...reminder, isEnabled: !reminder.isEnabled });
      setReminders((items) => items.map((item) => item.id === updated.id ? updated : item));
    });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await mutate(async () => {
      if (deleteTarget.kind === 'todo') {
        await api.deleteTodo(deleteTarget.item);
        setTodos((items) => items.filter((item) => item.id !== deleteTarget.item.id));
      } else if (deleteTarget.kind === 'reminder') {
        await api.deleteReminder(deleteTarget.item);
        setReminders((items) => items.filter((item) => item.id !== deleteTarget.item.id));
      } else {
        await api.deleteCalendarEvent(deleteTarget.item);
        setEvents((items) => items.filter((item) => item.id !== deleteTarget.item.id));
      }
      setDeleteTarget(null);
    });
  }

  async function savePreferences(themeId: string, themeMode: ThemeMode) {
    setPreferences((current) => ({ ...current, themeId, themeMode }));
    await mutate(async () => setPreferences(await api.savePreferences({ themeId, themeMode })));
  }

  async function logout() {
    await api.logout();
    setSettingsOpen(false);
    setAuthenticated(false);
  }

  const resetToday = () => {
    const value = todayParts();
    setSelectedDate(value);
    setVisibleMonth({ year: value.year, month: value.month });
    setMonthDirection(value.month >= visibleMonth.month ? 1 : -1);
  };
  const showToday = view === 'calendar' && (
    dateKey(selectedDate.year, selectedDate.month, selectedDate.day) !== dateKey(today.year, today.month, today.day) ||
    visibleMonth.year !== today.year || visibleMonth.month !== today.month
  );
  const pullRefresh = usePullToRefresh(
    () => loadAll(),
    authenticated !== true || busy || Boolean(editor || deleteTarget || settingsOpen),
  );

  if (loading && authenticated == null) return <LoadingScreen />;
  if (authenticated === false) return <LoginScreen onAuthenticated={() => loadAll()} />;

  return (
    <div className="app-shell" ref={pullRefresh.containerRef}>
      <div
        className={`pull-refresh-indicator${pullRefresh.refreshing ? ' refreshing' : ''}${pullRefresh.ready ? ' ready' : ''}`}
        style={{
          opacity: pullRefresh.pullDistance > 4 || pullRefresh.refreshing ? 1 : 0,
          transform: `translate(-50%, ${Math.max(-44, pullRefresh.pullDistance - 44)}px)`,
        }}
        aria-hidden="true"
      >
        <RefreshCw size={20} style={{ transform: `rotate(${pullRefresh.pullDistance * 4}deg)` }} />
      </div>
      <AppHeader
        view={view} syncState={syncState} searchOpen={searchOpen} search={search} syncCycle={syncCycle}
        onSearchOpen={() => setSearchOpen(true)} onSearchChange={setSearch}
        onSearchClose={() => { setSearch(''); setSearchOpen(false); }} onSettings={() => setSettingsOpen(true)}
      />
      <MainTabs view={view} counts={counts} onChange={changeView} />
      {view === 'todos' && <FilterRow filters={todoFilters} value={todoFilter} onChange={setTodoFilter} />}
      {view === 'reminders' && <FilterRow filters={reminderFilters} value={reminderFilter} onChange={setReminderFilter} />}

      {error && <button className="error-banner" type="button" onClick={() => void loadAll()}><RefreshCw size={16} />{error}</button>}

      <main className={`view-stage view-enter-${viewDirection > 0 ? 'right' : 'left'}`} key={view} {...mainSwipe}>
        {view === 'todos' && (
          <TodoView todos={todos} filter={todoFilter} search={search}
            onEdit={(item) => setEditor({ kind: 'todo', item })}
            onToggleDone={(item) => void toggleTodo(item, 'isDone')}
            onToggleEnabled={(item) => void toggleTodo(item, 'isEnabled')}
            onDelete={(item) => setDeleteTarget({ kind: 'todo', item })} />
        )}
        {view === 'reminders' && (
          <ReminderView reminders={reminders} filter={reminderFilter} search={search}
            onEdit={(item) => setEditor({ kind: 'reminder', item })}
            onToggle={(item) => void toggleReminder(item)}
            onCopy={(item) => setEditor({ kind: 'reminder', item, duplicate: true })}
            onDelete={(item) => setDeleteTarget({ kind: 'reminder', item })} />
        )}
        {view === 'calendar' && (
          <CalendarView events={events} search={search} visibleMonth={visibleMonth} selectedDate={selectedDate} monthDirection={monthDirection}
            onMonthChange={(value, direction) => { setMonthDirection(direction); setVisibleMonth(value); }}
            onDateChange={setSelectedDate}
            onEdit={(item) => setEditor({ kind: 'calendar', item })}
            onDelete={(item) => setDeleteTarget({ kind: 'calendar', item })} />
        )}
      </main>

      <div className="floating-actions">
        {showToday && <button className="today-fab" type="button" onClick={resetToday}>今天</button>}
        <button className="add-fab" type="button" aria-label="新增" onClick={() => setEditor({ kind: view === 'calendar' ? 'calendar' : view === 'reminders' ? 'reminder' : 'todo' })}>
          <Plus size={28} />
        </button>
      </div>

      {editor && <EditorSheet key={`${editor.kind}-${editor.item?.id ?? 'new'}-${editor.duplicate ? 'copy' : ''}`} editor={editor} prefillDate={selectedDate} busy={busy} onClose={() => setEditor(null)} onSave={saveEditor} />}
      {settingsOpen && <SettingsDialog themeId={preferences.themeId} mode={preferences.themeMode} busy={busy} onChange={savePreferences} onClose={() => setSettingsOpen(false)} onLogout={logout} />}
      {deleteTarget && <ConfirmDialog title={deleteTarget.item.title} busy={busy} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />}
    </div>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <img src="/icons/icon.svg" alt="" />
      <span className="loading-line" />
      <span className="loading-line short" />
    </main>
  );
}
